<template>
  <div id="app">
    <GameFinished v-if="gameFinished" />
    <span v-else>
      <div class="container" v-if="step === 'ageCheck'">
        <div class="content">
          <AgeCheck
            @ageValidated="
              handleAgeValidated();
            "
            @debug="debug()"
          />
        </div>
      </div>
      <ScratchArea
        v-if="step === 'scratch'"
        getPercentageCleared
        @percentage-update="updateScratchedPercentage"
      >
      </ScratchArea>
      <!-- mount together with ScratchArea -->
      <IceLayer
        :snow-washed-away="snowWashedAway"
        @iceCompletelyCrushed="startFinishAnimation = true"
        v-if="!startFinishAnimation"
      />
      <Finish
        :animation-started="startFinishAnimation"
        @finishGame="handleFinishGame()"
      />
    </span>
  </div>
</template>

<script>
import GameFinished from "@/components/GameFinished";
import AgeCheck from "@/components/AgeCheck";
import IceLayer from "@/components/IceLayer";
import ScratchArea from "@/components/ScratchArea";
import Finish from "@/components/Finish";
import i18n from "@/plugins/i18n";
import { useGameTracking } from "@/composables/useGameTracking";

export default {
  components: {
    GameFinished,
    AgeCheck,
    ScratchArea,
    IceLayer,
    Finish,
  },

  computed: {
    gameFinished() {
      // activate if you want to test the cheating mechanism
      // return this.$store.state.gameFinished
      return false;
    },
  },
  data() {
    return {
      step: "ageCheck",
      startFinishAnimation: false,
      snowWashedAway: false,
    };
  },
  mounted() {
    this.$store.dispatch("disableScroll");
    // check for language compaign and country query
    let lang = new URL(location.href).searchParams.get("lang");
    let country = new URL(location.href).searchParams.get("country");
    const campaignId = new URL(location.href).searchParams.get("campaignId") ?? "";

    if (lang) {
      i18n.locale = lang;
      this.$store.commit("setLanguage", lang);
    }

    if (country) {
      this.$store.commit("setCountry", country);
    }

    // set white glass color if in DACH region (language German)
    if (lang === "de") {
      this.$store.commit("setGlassColor", "white");
    }

    // Initialize KPI Tracker session
    const { kpiTracker, session } = useGameTracking();
    this.kpiTracker = kpiTracker;
    this.session = session;

    // Handle page unload - update session before user leaves
    this.handleBeforeUnload = () => {
      session.updateSessionProgressOnUnload(kpiTracker.getKpiData());
    };

    // Initialize session when coming to the page
    // IMPORTANT: Don't reset progress to 0 - it will be loaded from existing session if available
    console.log("[App] Initializing session on page load");
    session.getFingerprintAndInitializeSession(campaignId)
      .then(() => {
        const currentProgress = kpiTracker.progress?.value ?? 0;
        console.log(
          "[App] Session initialized, current progress:",
          currentProgress
        );
      })
      .catch((error) => {
        console.error("[App] Error initializing session on page load:", error);
      });

    // Add beforeunload listener
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    // Remove beforeunload listener
    if (this.handleBeforeUnload) {
      window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }
  },
  methods: {
    async updateScratchedPercentage(percentage) {
      if (percentage > 90) {
        this.step = "ice";
        this.snowWashedAway = true;
        
        // Update Session mit Step "ice" (aus App.vue)
        try {
          await this.session.updateSessionProgress(this.kpiTracker.getKpiData(), {
            step: "ice",
          });
        } catch (error) {
          console.error("Error updating session with ice step:", error);
        }
      }
    },
    handleAgeValidated() {
      this.step = "scratch";
      this.playBackgroundSound();
      // Note: Session Update für "scratch" erfolgt bereits in AgeCheck.vue
    },
    handleFinishGame() {
      this.step = "gameFinished";
      // Note: Session Update für "gameFinished" erfolgt bereits in Finish.vue
    },
    debug() {
      this.step = "ice";
      this.snowWashedAway = true;
      this.startFinishAnimation = true;
    },
    playBackgroundSound() {
      this.backgroundSound.volume = 0.3;
      this.backgroundSound.loop = true;
      this.backgroundSound.play();
    },
  },
};
</script>

<style scoped>
.h3 {
  color: #2c3e50;
  text-align: center;
}
.a {
  color: #2196f3;
}

.container {
  height: 100vh;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
